import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Save, Key, AlertCircle, CheckCircle, LogOut, Eye, EyeOff, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/api/api';
const ProfileSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // SuperAdmin - user selection for password change
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Check if user is SuperAdmin (no tenant_id means SuperAdmin)
  const isSuperAdmin = user?.role === 'SuperAdmin' || !user?.tenant_id;

  // Profile form state
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Password visibility state
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Fetch all users for SuperAdmin
  useEffect(() => {
    if (isSuperAdmin) {
      fetchAllUsers();
    }
  }, [isSuperAdmin]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/superadmin/all-users');
      if (response.data && response.data.users) {
        setAllUsers(response.data.users);
      } else if (Array.isArray(response.data)) {
        setAllUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback - just use current user
      setAllUsers([{ id: user?.id, email: user?.email, full_name: user?.full_name }]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    logout();
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/auth/update-profile', profileData);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    // Validate password strength
    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      setLoading(false);
      return;
    }

    try {
      // If SuperAdmin is changing another user's password
      if (isSuperAdmin && selectedUser && selectedUser.id !== user?.id) {
        await api.post('/superadmin/reset-user-password', {
          user_id: selectedUser.id,
          new_password: passwordData.new_password
        });
        setMessage({ type: 'success', text: `Password changed successfully for ${selectedUser.email || selectedUser.full_name}!` });
      } else {
        // Normal password change for own account
        await api.post('/auth/change-password', {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        });
        setMessage({ type: 'success', text: 'Password changed successfully!' });
      }
      
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to change password' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (selectedUserData) => {
    setSelectedUser(selectedUserData);
    setShowUserModal(false);
    setMessage({ type: '', text: '' });
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
  };

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: User },
    { id: 'security', label: 'Security', icon: Lock }
  ];

  // Determine which user's profile to show
  const displayUser = selectedUser || user;
  const isChangingOwnPassword = !selectedUser || selectedUser.id === user?.id;

  return (
    <div className="space-y-6" data-testid="profile-settings-page">
      {/* User Selection Modal for SuperAdmin */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Select which login to update:</h3>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {loadingUsers ? (
                <div className="p-4 text-center text-gray-500">Loading users...</div>
              ) : (
                allUsers.map((u, index) => (
                  <div
                    key={u.id || index}
                    onClick={() => handleUserSelect(u)}
                    className={`p-3 cursor-pointer hover:bg-blue-50 border-b last:border-b-0 ${
                      selectedUser?.id === u.id ? 'bg-blue-100' : ''
                    }`}
                  >
                    <p className="font-medium">{u.email || u.username || u.employee_id}</p>
                    {u.full_name && <p className="text-sm text-gray-500">{u.full_name}</p>}
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowUserModal(false)}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <User className="text-blue-600" size={32} />
            Profile Settings
          </h1>
          <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
        </div>
        
        {/* SuperAdmin - Select User Button */}
        {isSuperAdmin && (
          <Button 
            variant="outline" 
            onClick={() => setShowUserModal(true)}
            className="flex items-center gap-2"
          >
            <Users size={18} />
            Select User to Manage
          </Button>
        )}
      </div>

      {/* Selected User Info for SuperAdmin */}
      {isSuperAdmin && selectedUser && selectedUser.id !== user?.id && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-yellow-600" size={20} />
            <p className="text-yellow-800">
              Managing account: <span className="font-semibold">{selectedUser.email || selectedUser.full_name}</span>
            </p>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedUser(null)}
              className="ml-auto text-yellow-700 hover:text-yellow-800"
            >
              Back to my account
            </Button>
          </div>
        </div>
      )}

      {/* User Info Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <User size={40} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{displayUser?.full_name || 'User'}</h2>
              <p className="text-blue-100">{displayUser?.email || 'No email'}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-semibold">
                {displayUser?.role || user?.role || 'Admin'}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all duration-200 font-medium"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex gap-4 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMessage({ type: '', text: '' });
                  }}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Alert Messages */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`} data-testid="message-alert">
              {message.type === 'success' ? (
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              )}
              <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {message.text}
              </p>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 text-gray-400" size={20} />
                      <input
                        type="text"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your full name"
                        required
                        data-testid="full-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your email"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Account Information</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Role: <span className="font-semibold">{user?.role}</span>
                        </p>
                        <p className="text-sm text-blue-700">
                          Account Status: <span className="font-semibold">{user?.is_active !== false ? 'Active' : 'Inactive'}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProfileData({
                      full_name: user?.full_name || '',
                      email: user?.email || ''
                    });
                    setMessage({ type: '', text: '' });
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  <Save size={18} className="mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Change Password
                  {!isChangingOwnPassword && selectedUser && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (for {selectedUser.email || selectedUser.full_name})
                    </span>
                  )}
                </h3>
                <div className="space-y-4">
                  {/* Only show current password if changing own password */}
                  {isChangingOwnPassword && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                        <input
                          type={showPassword.current ? "text" : "password"}
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter current password"
                          required={isChangingOwnPassword}
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password *
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-3 text-gray-400" size={20} />
                      <input
                        type={showPassword.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter new password"
                        required
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password *
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-3 text-gray-400" size={20} />
                      <input
                        type={showPassword.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirm new password"
                        required
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-sm text-yellow-800 font-medium">Security Tips</p>
                        <ul className="text-sm text-yellow-700 mt-2 space-y-1 list-disc list-inside">
                          <li>Use a strong, unique password</li>
                          <li>Don't share your password with anyone</li>
                          <li>Change your password regularly</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setMessage({ type: '', text: '' });
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="change-password-btn"
                >
                  <Lock size={18} className="mr-2" />
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
